import { TranscriptionContext } from "@context/TranscriptionContext";
import { mintRoomWsToken } from "@transcription/dev/fixturesClient";
import { useLabRoomStore } from "@transcription/dev/labRoomStore";
import {
  useLiveTranscription,
  type StartOptions,
} from "@transcription/use-live-transcription";
import { devToolsEnabled } from "@utils/devTools";
import type { ReactNode } from "react";

/**
 * One live-transcription controller shared across every authenticated route, so
 * there is never more than one socket / recording / agent stream at a time —
 * starting one while another is active is a no-op.
 *
 * Owned by the router root, not the workspace, so navigating to
 * Calendar/Tasks/Notifications/Admin keeps the same socket and recording alive;
 * teardown happens on provider unmount (sign-out, user switch). Stored audio
 * anchors to the clinic — or the room in lab mode, mirroring Zuul — while
 * patient/visit attribution is the agent's job, never UI state.
 */
export const TranscriptionProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const labRoom = useLabRoomStore((s) => s.room);
  const transcription = useLiveTranscription();

  // Dev-only: a lab room selection (DevInjectPanel) makes every stream this
  // provider starts — injections and the live mic alike — behave as that
  // room's ambient device: the WS presents the room's identity (so the
  // backend stamps it exactly like a Zuul stream) and the stored audio
  // anchors to the room, as Zuul's uploads do.
  const roomMode = devToolsEnabled() && labRoom ? labRoom : null;
  const start = (options?: StartOptions) =>
    transcription.start({
      ...options,
      wsToken:
        options?.wsToken ??
        (roomMode ? () => mintRoomWsToken(roomMode.id) : undefined),
      about: options?.about ?? (roomMode ? [roomMode.id] : undefined),
    });

  return (
    <TranscriptionContext.Provider value={{ ...transcription, start }}>
      {children}
    </TranscriptionContext.Provider>
  );
};
